import React from "react";
import { CardContent, Typography, Stack, Tooltip, Box } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SoothCard from "components/base/SoothCard";
import { formatLargeNumber, getTooltipInfo } from "../utils/util";
import { METRIC_DESCRIPTIONS } from "../data/publisher.data";
import AccessibleIconButton from "components/base/AccessibleIconButton";

const getMetricDescription = (label) => {
  return METRIC_DESCRIPTIONS[label] || "";
};

export default function EngagementBox({ stats }) {
  return (
    <SoothCard
      spacing={2}
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        mb: 3,
      }}
    >
      {stats.map((stat) => {
        const hideChangeIndicator =
          stat.label === "Overall Rank" &&
          (stat.raw === null || stat.raw === undefined);
        const isPositive = stat.change >= 0;
        const hasChange = stat.hasChange;
        const isZero = stat.change === 0;
        const formattedValue = stat.format(stat.raw);
        const { isRounded, actualValue } = getTooltipInfo(
          stat.raw,
          formattedValue,
          stat.format
        );
        const description = getMetricDescription(stat.label);

        const renderChangeIndicator = () => {
          if (!hasChange) {
            return (
              <Tooltip followCursor title="No previous data">
                <Typography fontSize="1.5rem" color="text.secondary">
                  -
                </Typography>
              </Tooltip>
            );
          }

          if (isZero) {
            return (
              <Tooltip followCursor title="No previous changes">
                <Typography fontSize="1.5rem" color="text.secondary">
                  0%
                </Typography>
              </Tooltip>
            );
          }

          return (
            <Typography
              fontSize="1.5rem"
              color={isPositive ? "success.main" : "error.main"}
            >
              {isPositive ? "↑" : "↓"}
              {formatLargeNumber(Math.abs(stat.change))}
              {stat.label === "Overall Rank" ? "" : "%"}
            </Typography>
          );
        };

        const renderValue = () => {
          const valueElement = (
            <Typography fontSize="1.7rem" fontWeight="600">
              {formattedValue}
            </Typography>
          );

          if (isRounded && actualValue) {
            return (
              <Tooltip followCursor title={actualValue}>
                {valueElement}
              </Tooltip>
            );
          }

          return valueElement;
        };

        return (
          <React.Fragment key={stat.label}>
            <CardContent sx={{ textAlign: "center", minWidth: 140 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "start",
                  justifyContent: "center",
                  gap: 0.5,
                  mb: 1,
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: "500",
                    color: "text.primary",
                  }}
                >
                  {stat.label}
                </Typography>
                <AccessibleIconButton
                  size="small"
                  sx={{ p: 0.5 }}
                  title={description}
                  tooltipProps={{
                    placement: "top",
                  }}
                >
                  <InfoOutlinedIcon fontSize="small" color="primary" />
                </AccessibleIconButton>
              </Box>
              <Stack
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                {renderValue()}
                {!hideChangeIndicator && renderChangeIndicator()}
              </Stack>
            </CardContent>
          </React.Fragment>
        );
      })}
    </SoothCard>
  );
}
